import { Button, Card, Col, Modal, Input, Row, Tabs, Select, Spin } from "antd";
import React, { useState, useEffect } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import { IoMdAddCircleOutline } from "react-icons/io";
import BotIcon from "../../../assets/svg/bot.svg";
import DataSource from "../data_source";
import ChatWidget from "../chat_widget";
import { urls } from "helpers/urls";
import Request from "helpers/request";
import CommonNotification from "components/common/CommonNotification";

const { Option } = Select;

const Chatbot = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [activeKey, setActiveKey] = useState("ai-agents");
  const [newAgentName, setNewAgentName] = useState("");
  const [pageLoader, setPageLoader] = useState(false);
  const [refreshAppearance, setRefreshAppearance] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [openTabs, setOpenTabs] = useState([]);

  const showModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const onTabChange = (key) => {
    setActiveKey(key);
    if (key === "ai-agents") {
      setSelectedSiteId(null);
    }
  };

  const handleSiteSelection = (siteId) => {
    setSelectedSiteId(siteId);
    if (siteId) {
      const siteToOpen = sites.find((site) => site.id === siteId);
      if (siteToOpen) {
        setOpenTabs([siteToOpen]);
        setActiveKey(siteToOpen.id);
      } else {
        setOpenTabs([]);
        setActiveKey("ai-agents");
      }
    } else {
      setOpenTabs([]);
      setActiveKey("ai-agents");
    }
  };

  const handleCardClick = (agent) => {
    setSelectedSiteId(agent.id);
    setOpenTabs([agent]);
    setActiveKey(agent.id);
  };

  const getSites = async () => {
    try {
      setPageLoader(true);
      const response = await Request.get(urls.userSites);
      if (response.data && response.data.success) {
        setSites(response.data.data);
      } else {
        setSites([]);
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      setSites([]);
    } finally {
      setPageLoader(false);
    }
  };

  useEffect(() => {
    getSites();
  }, []);

  const removeTab = (targetKey) => {
    setOpenTabs([]);
    setSelectedSiteId(null);
    setActiveKey("ai-agents");
  };

  const deleteSite = (site_id) => {
    setPageLoader(true);
    return Request.delete(urls.createSite + `/${site_id}`)
      .then(() => {
        getSites();
        removeTab(site_id);
        if (selectedSiteId === site_id) {
          setSelectedSiteId(null);
        }
        CommonNotification.success("Site deleted successfully");
      })
      .catch(() => {
        CommonNotification.error("Error deleting site");
      })
      .finally(() => {
        setPageLoader(false);
      });
  };

  const handleDelete = (e, site_id) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Delete AI Agent',
      content: 'Are you sure you want to delete this agent?',
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: {
        style: {
          backgroundColor: '#2369d2',
          borderColor: '#2369d2',
          color: 'white'
        }
      },
      onOk: () => deleteSite(site_id),
    });
  };

  const createSite = () => {
    setPageLoader(true);
    Request.postJson(urls.createSite, { name: newAgentName })
      .then((response) => {
        getSites();
        closeModal();
        setNewAgentName("");
        CommonNotification.success("Site created successfully");
      })
      .catch((error) => {
        CommonNotification.error(
          error?.response?.data?.message || "Error creating site"
        );
      })
      .finally(() => {
        setPageLoader(false);
      });
  };

  return (
    <>
      {pageLoader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      <Card className="min-h-[700px]">
        <h1 className="text-[30px] font-[600] font-figtree chatbot-text mb-2 " style={{ fontFamily: "figtree" }}>Chatbot</h1>
        <Tabs
          className="custom-tab-wrap"
          type="editable-card"
          hideAdd
          activeKey={activeKey}
          onChange={onTabChange}
          onEdit={(targetKey, action) => {
            if (action === "remove") {
              removeTab(targetKey);
            }
          }}
          tabBarExtraContent={
            <div className="site-select-wrap">
              <Select
                style={{ width: 250 }}
                placeholder="Select a site"
                onChange={handleSiteSelection}
                value={selectedSiteId}
                allowClear
              >
                {sites.map((site) => (
                  <Option key={site.id} value={site.id}>
                    {site.name}
                  </Option>
                ))}
              </Select>
              <Button
                onClick={showModal}
              
                className="mb-1 flex items-center gap-2 rounded-[10px] bg-[#2369d2] px-4 py-5 text-white hover:bg-[#2369d2]/8 add-ai-button add-new-site-btn" 
              >
                <IoMdAddCircleOutline />
                Add AI Agent
              </Button>
            </div>
          }
        >
          <Tabs.TabPane tab="AI Agents" key="ai-agents" closable={false}>
            <Row gutter={[16, 16]}>
              {sites?.map((agent) => (
                <Col key={agent.id}>
                  <Card
                    style={{
                      width: 200,
                      textAlign: "center",
                      cursor: "pointer",
                      border: "1px solid #D9DBE4",
                      boxShadow: "0 2px 4px #0000001a",
                      fontFamily: "figtree",
                    }}
                    onClick={() => handleCardClick(agent)}
                    actions={[
                      <DeleteOutlined
                        key="delete"
                        onClick={(e) => handleDelete(e, agent.id)}
                      />,
                    ]}
                  >
                    <div
                      style={{
                        borderRadius: "50%",
                        width: "80px",
                        height: "80px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "0 auto 20px",
                      }}
                    >
                      <img
                        src={BotIcon}
                        alt="bot icon"
                        style={{ width: "120px", height: "120px" }}
                      />
                    </div>
                    <Card.Meta title={agent.name} />
                  </Card>
                </Col>
              ))}
            </Row>
          </Tabs.TabPane>
          {openTabs.map((agent) => (
            <Tabs.TabPane
              tab={
                <span style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={BotIcon}
                    alt="bot icon"
                    style={{ width: "35px", height: "35px", marginRight: "8px" }}
                  />
                  {agent.name}
                </span>
              }
              key={agent.id}
              closable={true}
              className="inner-custom-tabs-wrap"
            >
              <Tabs
                defaultActiveKey="data-source"
                onChange={(innerKey) => {
                  if (innerKey === "data-source") {
                    setRefreshAppearance((prev) => !prev);
                  }
                }}
              >
                <Tabs.TabPane tab="Data Source" key="data-source">
                  <DataSource
                    id={agent.id}
                    name={agent.name}
                    refreshTrigger={refreshAppearance}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Chat Widget" key="chat-widget">
                  <ChatWidget id={agent.id} name={agent.name} />
                </Tabs.TabPane>
              </Tabs>
            </Tabs.TabPane>
          ))}
        </Tabs>
      </Card>
      <Modal
        title="Add AI Agent"
        open={isModalOpen}
        onCancel={closeModal}
        footer={[
          <Button key="back" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="default"
            className="bg-customBlue text-white"
            onClick={createSite}
            disabled={newAgentName.trim() === ""}
          >
            Add New Site
          </Button>,
        ]}
      >
        <form layout="vertical">
          <div className="mb-[30px]">
            <label htmlFor="agentName" className="mb-[5px] block">
              Name
            </label>
            <Input
              placeholder="Enter agent name"
              value={newAgentName}
              onChange={(e) => setNewAgentName(e.target.value)}
              className="h-[40px]"
            />
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Chatbot;